import React from 'react';
import {
  Badge,
  HStack,
  Image,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { SportsCard } from '../ui/SportsCard';
import { ResponsiveTableContainer } from '../ui/ResponsiveTableContainer';
import { useAppColors } from '../../hooks/useAppColors';
import type { StageLeaderboard } from '../../services/mundial/mundialStageRankingService';

interface StageAccumulationSectionProps {
  stages: StageLeaderboard[];
  getUserAvatar: (name: string) => string | undefined;
  onParticipantClick: (participant: { uuid: string; name: string; points: number }, stage: StageLeaderboard) => void;
}

export const StageAccumulationSection: React.FC<StageAccumulationSectionProps> = ({
  stages,
  getUserAvatar,
  onParticipantClick,
}) => {
  const { subtle } = useAppColors();
  const rowHoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const podiumBg = useColorModeValue('gold.50', 'rgba(229, 160, 32, 0.12)');

  if (stages.length === 0) return null;

  return (
    <SportsCard>
      <Badge mb={3} colorScheme="pitch">
        Acumulación por etapa
      </Badge>
      <Text fontSize="xs" color={subtle} mb={4}>
        Puntos obtenidos en cada etapa del torneo. La suma de todas las etapas coincide con el
        acumulado general.
      </Text>

      <Tabs variant="soft-rounded" colorScheme="pitch" size="sm" isLazy>
        <TabList flexWrap="wrap" gap={1} mb={4}>
          {stages.map((stage) => (
            <Tab key={stage.id}>
              <HStack spacing={2}>
                <Text>{stage.title}</Text>
                {!stage.isComplete && (
                  <Badge size="sm" colorScheme="gray">
                    Parcial
                  </Badge>
                )}
              </HStack>
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {stages.map((stage) => {
            const leaderPoints = stage.rankings[0]?.points ?? 0;
            const hasPoints = stage.rankings.some((r) => r.points > 0);

            return (
              <TabPanel key={stage.id} px={0}>
                <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
                  <Text fontSize="sm" fontWeight="semibold">
                    {stage.title}
                  </Text>
                  <HStack spacing={2}>
                    {stage.prizePct && (
                      <Badge colorScheme="gold">{stage.prizePct} del pozo</Badge>
                    )}
                    {stage.isComplete ? (
                      <Badge colorScheme="green">Etapa completa</Badge>
                    ) : (
                      <Badge colorScheme="orange">En curso</Badge>
                    )}
                  </HStack>
                </HStack>

                {!hasPoints ? (
                  <Text fontSize="sm" color={subtle}>
                    Aún no hay puntos registrados en esta etapa.
                  </Text>
                ) : (
                  <ResponsiveTableContainer>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th w="48px">#</Th>
                          <Th>Participante</Th>
                          <Th isNumeric>Pts etapa</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {stage.rankings.map((user, index) => (
                          <Tr
                            key={user.uuid}
                            cursor="pointer"
                            onClick={() => onParticipantClick(user, stage)}
                            _hover={{ bg: rowHoverBg }}
                            bg={
                              index === 0 && user.points > 0 && user.points === leaderPoints
                                ? podiumBg
                                : undefined
                            }
                          >
                            <Td fontWeight="bold">{index + 1}</Td>
                            <Td>
                              <HStack>
                                <Text fontWeight={index < 3 ? 'semibold' : 'medium'}>
                                  {user.name}
                                </Text>
                                {getUserAvatar(user.name) && (
                                  <Image
                                    src={getUserAvatar(user.name)}
                                    alt=""
                                    boxSize="22px"
                                  />
                                )}
                              </HStack>
                            </Td>
                            <Td isNumeric fontWeight="bold">
                              {user.points}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </ResponsiveTableContainer>
                )}
              </TabPanel>
            );
          })}
        </TabPanels>
      </Tabs>
    </SportsCard>
  );
};
